export const kExternalStorageProviders = ['google_drive', 'dropbox', 'box', 'onedrive'] as const

export type ExternalStorageProvider = (typeof kExternalStorageProviders)[number]

export const kExternalStorageProvider = {
  GOOGLE_DRIVE: 'google_drive',
  DROPBOX: 'dropbox',
  BOX: 'box',
  ONEDRIVE: 'onedrive',
} as const satisfies Record<string, ExternalStorageProvider>

export const kExternalStorageProviderDisplayName: Record<ExternalStorageProvider, string> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: 'Google Drive',
  [kExternalStorageProvider.DROPBOX]: 'Dropbox',
  [kExternalStorageProvider.BOX]: 'Box',
  [kExternalStorageProvider.ONEDRIVE]: 'Microsoft OneDrive',
}

/**
 * Single source of truth for provider availability in the UI.
 */
const kEnabledExternalStorageProvidersByMode: Record<string, readonly ExternalStorageProvider[]> = {
  development: kExternalStorageProviders,
  staging: kExternalStorageProviders,
  ephemeral: kExternalStorageProviders,
  // For initial production rollout [kExternalStorageProvider.BOX, kExternalStorageProvider.ONEDRIVE]
  production: [],
}

export function getEnabledExternalStorageProvidersForMode(mode: string): readonly ExternalStorageProvider[] {
  return kEnabledExternalStorageProvidersByMode[mode] ?? []
}

const kEnabledExternalStorageProviderSet = new Set<ExternalStorageProvider>(getEnabledExternalStorageProvidersForMode(import.meta.env.MODE))

export const kExternalStorageProviderEnabled: Record<ExternalStorageProvider, boolean> = {
  [kExternalStorageProvider.GOOGLE_DRIVE]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.GOOGLE_DRIVE),
  [kExternalStorageProvider.DROPBOX]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.DROPBOX),
  [kExternalStorageProvider.BOX]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.BOX),
  [kExternalStorageProvider.ONEDRIVE]: kEnabledExternalStorageProviderSet.has(kExternalStorageProvider.ONEDRIVE),
}

export const kEnabledExternalStorageProviders = kExternalStorageProviders.filter((provider) => kExternalStorageProviderEnabled[provider])

export function kIsExternalStorageProvider(value: string | null): value is ExternalStorageProvider {
  return kEnabledExternalStorageProviders.some((provider) => provider === value)
}

export function kIsExternalStorageProviderEnabled(provider: ExternalStorageProvider): boolean {
  return kExternalStorageProviderEnabled[provider]
}

export const kHasEnabledExternalStorageProviders = kEnabledExternalStorageProviders.length > 0

export function sortExternalStorageProvidersForDisplay(
  providers: readonly ExternalStorageProvider[],
  enabledProviders: readonly ExternalStorageProvider[] = kEnabledExternalStorageProviders
): ExternalStorageProvider[] {
  const enabledProviderSet = new Set(enabledProviders)

  return [...providers].sort((providerA, providerB) => {
    const providerAEnabled = enabledProviderSet.has(providerA)
    const providerBEnabled = enabledProviderSet.has(providerB)

    if (providerAEnabled !== providerBEnabled) {
      return providerAEnabled ? -1 : 1
    }

    return kExternalStorageProviderDisplayName[providerA].localeCompare(kExternalStorageProviderDisplayName[providerB])
  })
}

export const kVisibleExternalStorageProviders = sortExternalStorageProvidersForDisplay(kExternalStorageProviders)
